import React, { useState, useEffect } from 'react';
import { 
  Bell, 
  X, 
  AlertTriangle, 
  CheckCircle, 
  Info, 
  AlertCircle,
  Users,
  CreditCard,
  Gamepad2,
  Shield,
  Activity,
  Wifi,
  WifiOff
} from 'lucide-react';
import { useAdminPusher, AdminEvent, GameEvent, PaymentEvent } from '../services/pusher';
import { toastService } from '../utils/toast';

interface NotificationItemProps {
  event: AdminEvent | GameEvent | PaymentEvent;
  onDismiss: () => void;
}

const NotificationItem: React.FC<NotificationItemProps> = ({ event, onDismiss }) => {
  const getIcon = () => {
    if ('type' in event) {
      // AdminEvent
      switch (event.type) {
        case 'user_action':
          return <Users className="h-5 w-5 text-blue-500" />;
        case 'payment':
          return <CreditCard className="h-5 w-5 text-green-500" />;
        case 'system':
          return <Activity className="h-5 w-5 text-orange-500" />;
        case 'security':
          return <Shield className="h-5 w-5 text-red-500" />;
        default:
          return <Info className="h-5 w-5 text-gray-500" />;
      }
    } else if ('gameId' in event) {
      // GameEvent
      return <Gamepad2 className="h-5 w-5 text-purple-500" />;
    } else {
      // PaymentEvent
      return <CreditCard className="h-5 w-5 text-green-500" />;
    }
  };

  const getSeverityColor = () => {
    if ('severity' in event) {
      switch (event.severity) {
        case 'critical':
          return 'border-red-500 bg-red-50 dark:bg-red-900';
        case 'high':
          return 'border-orange-500 bg-orange-50 dark:bg-orange-900';
        case 'medium':
          return 'border-yellow-500 bg-yellow-50 dark:bg-yellow-900';
        case 'low':
          return 'border-blue-500 bg-blue-50 dark:bg-blue-900';
        default:
          return 'border-gray-500 bg-gray-50 dark:bg-gray-900';
      }
    }
    return 'border-gray-500 bg-gray-50 dark:bg-gray-900';
  };

  const getTitle = () => {
    if ('type' in event) {
      // AdminEvent
      switch (event.type) {
        case 'user_action':
          return 'Felhasználói Művelet';
        case 'payment':
          return 'Fizetési Esemény';
        case 'system':
          return 'Rendszer Riasztás';
        case 'security':
          return 'Biztonsági Esemény';
        default:
          return 'Ismeretlen Esemény';
      }
    } else if ('gameId' in event) {
      // GameEvent
      switch (event.action) {
        case 'started':
          return 'Játék Elkezdődött';
        case 'completed':
          return 'Játék Befejezve';
        case 'abandoned':
          return 'Játék Megszakítva';
        case 'ball_pocketed':
          return 'Golyó Lyukba Esett';
        case 'turn_change':
          return 'Kör Váltás';
        default:
          return 'Játék Esemény';
      }
    } else {
      // PaymentEvent
      switch (event.status) {
        case 'completed':
          return 'Sikeres Fizetés';
        case 'failed':
          return 'Sikertelen Fizetés';
        case 'pending':
          return 'Függő Fizetés';
        case 'refunded':
          return 'Visszatérített Fizetés';
        default:
          return 'Fizetési Esemény';
      }
    }
  };

  const getMessage = () => {
    if ('type' in event) {
      // AdminEvent
      if (event.data.message) {
        return event.data.message;
      }
      if (event.data.action) {
        return `${event.data.action} - ${event.data.userId || 'N/A'}`;
      }
      return JSON.stringify(event.data);
    } else if ('gameId' in event) {
      // GameEvent
      return `Játék ID: ${event.gameId} - Játékosok: ${event.players.join(', ')}`;
    } else {
      // PaymentEvent
      return `${event.userId} - $${event.amount} (${event.paymentMethod})`;
    }
  };

  return (
    <div className={`relative p-4 rounded-lg border-l-4 ${getSeverityColor()} mb-3 transition-all duration-200 hover:shadow-md`}>
      <div className="flex items-start justify-between">
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0 mt-1">
            {getIcon()}
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
              {getTitle()}
            </h4>
            <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
              {getMessage()}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              {new Date(event.timestamp).toLocaleString()}
            </p>
          </div>
        </div>
        <button
          onClick={onDismiss}
          className="flex-shrink-0 ml-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

interface RealtimeNotificationsProps {
  isMinimized?: boolean;
  onToggleMinimize?: () => void;
}

const RealtimeNotifications: React.FC<RealtimeNotificationsProps> = ({ 
  isMinimized = false, 
  onToggleMinimize 
}) => {
  const { 
    isConnected, 
    events, 
    gameEvents, 
    paymentEvents, 
    notificationPermission,
    triggerTestEvents,
    getConnectionState,
    getSubscribedChannels,
    getBindingsCount,
    requestNotificationPermission,
    showNotification
  } = useAdminPusher();

  const [dismissedEvents, setDismissedEvents] = useState<Set<string>>(new Set());
  const [showDebugInfo, setShowDebugInfo] = useState(false);

  // Összes esemény kombinálása és rendezése
  const allEvents = [
    ...events.map((e, index) => ({ ...e, id: `admin-${e.timestamp}-${index}` })),
    ...gameEvents.map((e, index) => ({ ...e, id: `game-${e.timestamp}-${index}` })),
    ...paymentEvents.map((e, index) => ({ ...e, id: `payment-${e.timestamp}-${index}` }))
  ]
    .filter(event => !dismissedEvents.has(event.id))
    .sort((a, b) => b.timestamp - a.timestamp)
    .slice(0, 10); // Max 10 esemény megjelenítése

  const handleDismiss = (eventId: string) => {
    setDismissedEvents(prev => new Set(Array.from(prev).concat(eventId)));
  };

  const handleDismissAll = () => {
    const allEventIds = allEvents.map(e => e.id);
    setDismissedEvents(prev => new Set(Array.from(prev).concat(allEventIds)));
  };

  // Toast értesítések kritikus eseményekre
  useEffect(() => {
    events.forEach((event, index) => {
      if (event.severity === 'critical' || event.severity === 'high') {
        const eventId = `admin-${event.timestamp}-${index}`;
        if (!dismissedEvents.has(eventId)) {
          if (event.severity === 'critical') {
            toastService.error(`🚨 ${event.data.message || 'Kritikus rendszer esemény'}`);
          } else {
            toastService.warning(`⚠️ ${event.data.message || 'Magas prioritású esemény'}`);
          }
        }
      }
    });
  }, [events, dismissedEvents]);

  if (isMinimized) {
    return (
      <div className="fixed bottom-4 right-4 z-50">
        <button
          onClick={onToggleMinimize}
          className={`p-3 rounded-full shadow-lg transition-all duration-200 ${
            isConnected 
              ? 'bg-green-500 hover:bg-green-600' 
              : 'bg-red-500 hover:bg-red-600'
          } text-white`}
        >
          <Bell className="h-6 w-6" />
          {allEvents.length > 0 && (
            <span className="absolute -top-2 -right-2 bg-red-600 text-white text-xs rounded-full h-6 w-6 flex items-center justify-center">
              {allEvents.length > 9 ? '9+' : allEvents.length}
            </span>
          )}
        </button>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Valós Idejű Értesítések
            </h3>
            <div className="flex items-center space-x-3">
              {isConnected ? (
                <div className="flex items-center space-x-1 text-green-600 dark:text-green-400">
                  <Wifi className="h-4 w-4" />
                  <span className="text-xs">Kapcsolódva</span>
                </div>
              ) : (
                <div className="flex items-center space-x-1 text-red-600 dark:text-red-400">
                  <WifiOff className="h-4 w-4" />
                  <span className="text-xs">Nincs kapcsolat</span>
                </div>
              )}
              
              {/* Notification Permission Status */}
              <div className="flex items-center space-x-1">
                {notificationPermission === 'granted' ? (
                  <div className="flex items-center space-x-1 text-green-600 dark:text-green-400">
                    <Bell className="h-4 w-4" />
                    <span className="text-xs">Értesítések</span>
                  </div>
                ) : notificationPermission === 'denied' ? (
                  <div className="flex items-center space-x-1 text-red-600 dark:text-red-400">
                    <Bell className="h-4 w-4" />
                    <span className="text-xs">Letiltva</span>
                  </div>
                ) : (
                  <button
                    onClick={async () => {
                      const permission = await requestNotificationPermission();
                      if (permission === 'granted') {
                        showNotification('🎉 Értesítések Aktiválva!', {
                          body: 'Most már böngésző értesítéseket fogsz kapni.',
                          tag: 'permission-granted'
                        });
                      }
                    }}
                    className="flex items-center space-x-1 text-orange-600 dark:text-orange-400 hover:text-orange-800 dark:hover:text-orange-200"
                  >
                    <Bell className="h-4 w-4" />
                    <span className="text-xs">Engedélyezés</span>
                  </button>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowDebugInfo(!showDebugInfo)}
              className="text-xs px-2 py-1 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
            >
              Debug
            </button>
            <button
              onClick={triggerTestEvents}
              className="text-xs px-2 py-1 rounded bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-800"
            >
              Test Events
            </button>
            {notificationPermission === 'granted' && (
              <button
                onClick={() => showNotification('🧪 Teszt Értesítés', {
                  body: 'Ez egy teszt böngésző értesítés!',
                  tag: 'test-notification'
                })}
                className="text-xs px-2 py-1 rounded bg-purple-100 dark:bg-purple-900 text-purple-600 dark:text-purple-300 hover:bg-purple-200 dark:hover:bg-purple-800"
              >
                Test Notification
              </button>
            )}
            {allEvents.length > 0 && (
              <button
                onClick={handleDismissAll}
                className="text-xs px-2 py-1 rounded bg-red-100 dark:bg-red-900 text-red-600 dark:text-red-300 hover:bg-red-200 dark:hover:bg-red-800"
              >
                Összes törlése
              </button>
            )}
            {onToggleMinimize && (
              <button
                onClick={onToggleMinimize}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="card-body">
        {showDebugInfo && (
          <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Debug Információk</h4>
            <div className="text-xs text-gray-600 dark:text-gray-300 space-y-1">
              <p><strong>Kapcsolat állapot:</strong> {getConnectionState()}</p>
              <p><strong>Böngésző értesítések:</strong> {notificationPermission}</p>
              <p><strong>Feliratkozott csatornák:</strong> {getSubscribedChannels().join(', ')}</p>
              <p><strong>Aktív bindings:</strong> {getBindingsCount()}</p>
              <p><strong>Admin események:</strong> {events.length}</p>
              <p><strong>Játék események:</strong> {gameEvents.length}</p>
              <p><strong>Fizetési események:</strong> {paymentEvents.length}</p>
              <p><strong>Elrejtett események:</strong> {dismissedEvents.size}</p>
            </div>
          </div>
        )}

        {allEvents.length === 0 ? (
          <div className="text-center py-8">
            <Bell className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-400">Nincsenek új értesítések</p>
            <button
              onClick={triggerTestEvents}
              className="mt-2 text-sm text-blue-600 dark:text-blue-400 hover:underline"
            >
              Teszt események kiváltása
            </button>
          </div>
        ) : (
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {allEvents.map((event) => (
              <NotificationItem
                key={event.id}
                event={event}
                onDismiss={() => handleDismiss(event.id)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default RealtimeNotifications; 